import { API_CONFIG, apiService } from '../config/api';
import type { CopilotAnswer, CopilotContext } from '../components/researcher/copilot/copilotTypes';

export interface CopilotQuery {
  studyId: string;
  question: string;
  history?: Array<{ role: string; content: string }>;
  context?: CopilotContext;
}

export interface CopilotHandlers {
  onToken?: (delta: string) => void;
  onResult?: (answer: CopilotAnswer) => void;
  onError?: (message: string) => void;
  /** Fired on a `restart` event: the failed first attempt's preamble should be cleared before the retry streams. */
  onRestart?: () => void;
  /**
   * Fired with the in-range record count on a `count` event (just before a chart result), and with
   * `null` to CLEAR a received count on restart / abort / a fallback result — so a count never
   * precedes a non-chart or fallback answer.
   */
  onCount?: (count: number | null) => void;
  onProgress?: (progress: { phase: string; completed: number; total: number; label: string }) => void;
  /** Fired when the request is aborted (Stop) — distinct from an error or a missing answer. */
  onAbort?: () => void;
  signal?: AbortSignal;
}

/**
 * Streams a Copilot answer over SSE. Hand-rolled (not via apiService, which assumes JSON):
 * POSTs the question with the Bearer token, then reads `response.body` as a chunk-safe stream,
 * parsing `event: token | result | error` frames and dispatching to the handlers.
 */
export async function streamCopilotQuery(query: CopilotQuery, handlers: CopilotHandlers = {}): Promise<void> {
  const token = await apiService.getAuthToken();
  let res: Response;
  try {
    res = await fetch(`${API_CONFIG.BASE_URL}/api/copilot/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(query),
      signal: handlers.signal,
    });
  } catch (e: any) {
    if (e?.name === 'AbortError') { handlers.onCount?.(null); handlers.onAbort?.(); }
    else handlers.onError?.(e?.message || 'Network error');
    return;
  }

  if (!res.ok || !res.body) {
    handlers.onError?.(`Request failed (${res.status})`);
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) >= 0) {
        dispatch(buffer.slice(0, sep), handlers);
        buffer = buffer.slice(sep + 2);
      }
    }
    if (buffer.trim()) dispatch(buffer, handlers);
  } catch (e: any) {
    if (e?.name === 'AbortError') { handlers.onCount?.(null); handlers.onAbort?.(); }
    else handlers.onError?.(e?.message || 'Stream error');
  }
}

function dispatch(raw: string, handlers: CopilotHandlers) {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
  }
  const data = dataLines.join('\n');

  if (event === 'token') {
    // Token frames are JSON-encoded server-side so newline-bearing deltas survive the SSE `data:`.
    if (!data) return;
    try {
      const decoded = JSON.parse(data);
      if (typeof decoded === 'string' && decoded) handlers.onToken?.(decoded);
    } catch {
      handlers.onToken?.(data); // tolerate a legacy/plain frame rather than dropping it
    }
  } else if (event === 'count') {
    // The in-range record count, emitted just before a chart result so the readout can lead.
    try {
      const { count } = JSON.parse(data);
      if (typeof count === 'number' && Number.isFinite(count)) handlers.onCount?.(count);
    } catch {
      /* a malformed count is non-fatal — the chart still builds without the readout lead */
    }
  } else if (event === 'restart') {
    // The first attempt is being discarded; clear any count it may have led with.
    handlers.onCount?.(null);
    handlers.onRestart?.();
  } else if (event === 'progress') {
    try {
      const progress = JSON.parse(data);
      if (progress && typeof progress.phase === 'string' && Number.isInteger(progress.completed) && Number.isInteger(progress.total) && typeof progress.label === 'string') {
        handlers.onProgress?.(progress);
      }
    } catch {
      /* progress is optional UI feedback and never affects the answer */
    }
  } else if (event === 'result') {
    try {
      const answer = JSON.parse(data) as CopilotAnswer;
      // A fallback never carries the chart's count — clear any count received before it.
      if (answer && (answer as { kind?: string }).kind === 'fallback') handlers.onCount?.(null);
      handlers.onResult?.(answer);
    } catch {
      handlers.onError?.('Malformed result payload');
    }
  } else if (event === 'error') {
    let msg = data;
    try {
      msg = JSON.parse(data).message ?? data;
    } catch {
      /* keep raw */
    }
    // A `count` may have led this turn before it errored — clear it so a stale figure can't survive into
    // a cached-fallback chart (keeps the "count never precedes a non-chart/fallback result" invariant).
    handlers.onCount?.(null);
    handlers.onError?.(msg);
  }
}
